/**
 * Wartenden Service-Worker aktivieren und neu laden — EIN Weg für beide
 * Anlässe (26.09.2026, Ticket 2653).
 *
 * Anlass: Ein verdeckter Chrome-Tab lud sich alle fünf Minuten neu, im
 * Journal „[update] still nachgeladen (takt) auf" ohne Version. Ein neuer
 * Worker wartete, das stille Nachladen lud aber nur die Seite neu, ohne ihn
 * zu aktivieren. Ein Neuladen allein lässt einen wartenden Worker warten —
 * die neue Seite fand ihn wieder, meldete wieder „neu" und lud fünf Minuten
 * später wieder. Der Knopf „Neue Version" dagegen schickte „skip-waiting".
 *
 * ⚠️ Zwei Wege für dieselbe Sache waren die Ursache. Knopf UND stilles
 *    Nachladen rufen darum `aktivierenUndNeuLaden()`; nur dieses Modul
 *    spricht „skip-waiting" und entscheidet, wann nach dem Wechsel geladen
 *    wird.
 */

interface WechselUmgebung {
  /** `navigator.serviceWorker`, oder `undefined` ohne Unterstützung. */
  container: ServiceWorkerContainer | undefined;
  /** Die Seite neu laden (`location.reload()`). */
  neuLaden: () => void;
  /**
   * Es wartet kein Worker: der bisherige Weg (Server erreichbar? Registrierung
   * auffrischen, dann neu laden).
   */
  ohneWartendenNeuLaden: () => void;
  /** Darf das Sicherheitsnetz hart neu laden? Im Anruf nicht. */
  notladenErlaubt: () => boolean;
  /** Frist des Sicherheitsnetzes in ms (Pre.660: 3,5 s). */
  frist?: number;
  /** Wie lange auf einen noch installierenden Worker gewartet wird (Standard 4 s). */
  installFrist?: number;
}

export interface WorkerWechsel {
  /** Läuft das Neuladen schon? Dann nichts mehr anstoßen. */
  readonly laedt: boolean;
  /**
   * Knopf und stilles Nachladen: wartet ein Worker, übernimmt er
   * („skip-waiting") und `beiWechsel()` lädt neu; wartet keiner, lädt der
   * bisherige Weg neu.
   */
  aktivierenUndNeuLaden(): Promise<"aktiviert" | "ohne-worker">;
  /**
   * Für `controllerchange`. `true` heißt: erledigt (lädt neu oder lud schon).
   * `false` heißt: der Wechsel kam ohne Anforderung — der Aufrufer zeigt den
   * Knopf, die Seite wird nicht unter den Händen weggezogen (25.07.).
   */
  beiWechsel(): boolean;
  /**
   * Ein wartender Worker mit DERSELBEN Fassung wie die Seite: still übernehmen lassen, ohne Neuladen und ohne Knopf
   * (lib/worker-version.ts). `false`, wenn die Nachricht nicht ging.
   */
  uebernehmenStill(worker: Pick<ServiceWorker, "postMessage">): boolean;
}

/** Installiert gerade ein Worker? Dann warten, bis er „wartend“ ist (oder die Frist um ist). Sonst null. */
function warteAufInstallation(reg: ServiceWorkerRegistration, frist: number): Promise<ServiceWorker | null> {
  const neu = reg.installing;
  if (!neu) return Promise.resolve(reg.waiting ?? null);
  return new Promise((fertig) => {
    let erledigt = false;
    const ende = (): void => {
      if (erledigt) return;
      erledigt = true;
      globalThis.clearTimeout(uhr);
      neu.removeEventListener("statechange", pruefe);
      fertig(reg.waiting ?? null);
    };
    const pruefe = (): void => {
      if (neu.state !== "installing") ende();
    };
    const uhr = globalThis.setTimeout(ende, frist);
    neu.addEventListener("statechange", pruefe);
    pruefe();
  });
}

export function erzeugeWorkerWechsel(u: WechselUmgebung): WorkerWechsel {
  let angefordert = false;
  let laedt = false;

  /*
   * Sicherheitsnetz (Pre.660): Feuert `controllerchange` nicht, weil der
   * Worker tot ist, nach der Frist alle Worker abmelden und hart neu laden.
   */
  const notladen = (container: ServiceWorkerContainer): void => {
    if (laedt) return;
    if (!u.notladenErlaubt()) {
      // Prüfung zu pre.3334: sonst bliebe die Anforderung stehen, und ein
      // späterer Wechsel (etwa aus einem anderen Tab) lüde ohne Zutun neu.
      angefordert = false;
      return;
    }
    laedt = true;
    void container
      .getRegistrations()
      .then((rs) => Promise.all(rs.map((r) => r.unregister())))
      .catch(() => undefined)
      .finally(() => u.neuLaden());
  };

  return {
    get laedt() {
      return laedt;
    },

    async aktivierenUndNeuLaden() {
      // Die Anforderung — ab hier darf der Worker-Wechsel sofort neu laden.
      angefordert = true;
      const container = u.container;
      /*
       * ⚠️ Den AKTUELL wartenden Worker der Registrierung fragen, keine
       *    gemerkte Referenz: die kann längst übernommen haben oder
       *    verworfen sein, dann ginge „skip-waiting" ins Leere (Pre.658).
       */
      const reg = container
        ? await container.getRegistration().catch(() => undefined)
        : undefined;
      let ziel = reg?.waiting ?? null;
      /*
       * ⚠️ Wartet keiner, ist ein frischer Worker oft NOCH AM INSTALLIEREN (jeder Bau schreibt eine neue sw.js). Lüde die
       *    Seite jetzt neu, käme sie mit neuer Fassung und der Worker danach „wartend“ — der Knopf stünde wieder da
       *    (29.10.2026, Martin). Darum: erst auffrischen und das Ende der Installation abwarten (kurze Frist), dann übernehmen.
       */
      if (reg && !ziel) {
        await reg.update().catch(() => undefined);
        ziel = reg.waiting ?? (await warteAufInstallation(reg, u.installFrist ?? 4000));
      }
      if (!container || !ziel) {
        /*
         * Prüfung zu pre.3334: Der bisherige Weg lädt selbst neu — oder, wenn
         * der Server nicht erreichbar ist, gar nicht. Die Anforderung darf
         * dann nicht stehen bleiben: aktiviert später ein anderer Tab einen
         * Worker, lüde diese Seite sonst ohne Zutun neu, auch im Anruf.
         */
        angefordert = false;
        u.ohneWartendenNeuLaden();
        return "ohne-worker";
      }
      try {
        ziel.postMessage("skip-waiting");
      } catch {
        /* Worker evtl. schon verworfen — das Sicherheitsnetz greift */
      }
      globalThis.setTimeout(() => notladen(container), u.frist ?? 3500);
      return "aktiviert";
    },

    uebernehmenStill(worker) {
      try {
        worker.postMessage("skip-waiting");
        return true;
      } catch {
        return false;
      }
    },

    beiWechsel() {
      if (laedt) return true;
      if (!angefordert) return false;
      // Dieselbe Sperre wie beim Notladen: nicht mitten im Anruf.
      if (!u.notladenErlaubt()) {
        angefordert = false;
        return false;
      }
      laedt = true;
      u.neuLaden();
      return true;
    },
  };
}
